
import React, { useState, useEffect } from 'react';
import { X, Image } from 'lucide-react';
import { toast } from 'sonner';
import { FormLabel } from '@/components/ui/form';

interface ImageUploaderProps {
  imagePreviewUrls: string[];
  setImagePreviewUrls: (urls: string[]) => void;
  setImageFiles: (files: File[]) => void;
  imageFiles: File[];
  form: any;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  imagePreviewUrls,
  setImagePreviewUrls,
  setImageFiles,
  imageFiles,
  form,
}) => {
  const [imageLoadErrors, setImageLoadErrors] = useState<Record<string, boolean>>({});
  
  useEffect(() => {
    // Update form value whenever preview URLs change
    if (form) {
      console.log('Updating form image_urls with:', imagePreviewUrls);
      form.setValue('image_urls', imagePreviewUrls);
    }
  }, [imagePreviewUrls, form]);
  
  // Initialize from form values on component mount
  useEffect(() => {
    const existingUrls = form.getValues('image_urls');
    if (existingUrls && Array.isArray(existingUrls) && existingUrls.length > 0) {
      console.log('Found existing image URLs in form:', existingUrls);
      // Only update if the current preview URLs are empty and form has values
      if (imagePreviewUrls.length === 0) {
        setImagePreviewUrls(existingUrls);
        
        // Initialize load error tracking for these URLs
        const initialErrors: Record<string, boolean> = {};
        existingUrls.forEach(url => {
          initialErrors[url] = false;
        });
        setImageLoadErrors(initialErrors);
      }
    }
  }, [form, setImagePreviewUrls, imagePreviewUrls.length]);

  const handleImageError = (url: string) => {
    console.log('Image failed to load:', url);
    setImageLoadErrors(prev => ({ ...prev, [url]: true }));
    toast.error("Failed to load image. It may be unavailable or have been deleted.");
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('Image upload triggered');
    const files = e.target.files;
    if (!files) return;

    const newFiles: File[] = [];
    const newPreviewUrls: string[] = [...imagePreviewUrls];

    // Limit to 3 images total
    const remainingSlots = 3 - imagePreviewUrls.length;
    if (remainingSlots <= 0) {
      toast.error('Maximum of 3 images allowed');
      return;
    }

    // Process each selected file (up to the remaining slots)
    for (let i = 0; i < Math.min(files.length, remainingSlots); i++) {
      const file = files[i];

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        continue;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} exceeds the 5MB size limit`);
        continue;
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      newPreviewUrls.push(previewUrl);
      newFiles.push(file);
      
      // Add to error tracking
      setImageLoadErrors(prev => ({ ...prev, [previewUrl]: false }));
    }

    setImageFiles([...imageFiles, ...newFiles]);
    setImagePreviewUrls(newPreviewUrls);
    
    console.log('Images updated, new count:', newPreviewUrls.length);
  };

  const removeImage = (index: number) => {
    console.log('Removing image at index:', index);
    // Remove from preview URLs
    const newPreviewUrls = [...imagePreviewUrls];
    const urlToRemove = newPreviewUrls[index];
    newPreviewUrls.splice(index, 1);
    setImagePreviewUrls(newPreviewUrls);
    
    // Update error tracking
    setImageLoadErrors(prev => {
      const updated = { ...prev };
      delete updated[urlToRemove];
      return updated;
    });
    
    // Only revoke object URL and remove from files array if it's a new upload (blob URL)
    if (urlToRemove?.startsWith('blob:')) {
      URL.revokeObjectURL(urlToRemove);
      
      // Find the corresponding file and remove it
      const newFiles = [...imageFiles];
      const fileIndex = newFiles.findIndex(file => 
        urlToRemove.includes(encodeURIComponent(file.name))
      );
      
      if (fileIndex !== -1) {
        newFiles.splice(fileIndex, 1);
        setImageFiles(newFiles);
      }
    }
    
    console.log('Image removed, new count:', newPreviewUrls.length);
  };

  return (
    <div className="space-y-4">
      <FormLabel>Images (max 3)</FormLabel>
      <div className="flex flex-wrap gap-4 mb-2">
        {imagePreviewUrls.map((url, index) => (
          <div key={index} className="relative w-24 h-24 border rounded-md overflow-hidden group">
            {imageLoadErrors[url] ? (
              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                <Image className="h-8 w-8 text-gray-400" />
              </div>
            ) : (
              <img
                src={url}
                alt={`Preview ${index + 1}`}
                className="w-full h-full object-cover"
                onError={() => handleImageError(url)}
              />
            )}
            <button
              type="button"
              onClick={() => removeImage(index)}
              className="absolute top-0 right-0 bg-black bg-opacity-50 p-1 rounded-bl-md opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="h-4 w-4 text-white" />
            </button>
          </div>
        ))}

        {imagePreviewUrls.length < 3 && (
          <label className="flex flex-col items-center justify-center w-24 h-24 border-2 border-dashed rounded-md border-gray-300 cursor-pointer hover:bg-gray-50">
            <Image className="h-8 w-8 text-gray-400" />
            <span className="mt-1 text-xs text-center text-gray-500">Upload</span>
            <input
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={handleImageChange}
              multiple={3 - imagePreviewUrls.length > 1}
              id="image-upload"
              name="image-upload"
            />
          </label>
        )}
      </div>
    </div>
  );
};

export default ImageUploader;
